import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@repo/ui/components/Base/Dialog";
import { useEffect, useState } from "react";
import { UserImportUploadStepForm } from "./userImport/UserImportUploadStepForm";
import type { ImportDataUploadResult, UserDuplicate } from "@repo/types";
import { UserImportBindColumnsStepForm } from "./userImport/UserImportBindColumnsStepForm";
import type { UserImportData } from "./UsersPreviewTable";
import { UserImportPreviewStepForm } from "./userImport/UserImportPreviewStepForm";
import { UserImportFinishStep } from "./userImport/UserImportFinishStep";
import type { UserImportBindingConfig } from "@/services/userImportService";
import { CircleArrowUp } from "lucide-react";
import { Button } from "@repo/ui/components/Base/Button";

export const ImportUsersDialog = ({ tenantId }: { tenantId: string }) => {
  const [open, setOpen] = useState(false);
  const [importedFile, setImportedFile] =
    useState<ImportDataUploadResult | null>(null);
  const [csvHeaders, setCsvHeaders] = useState<string[]>([]);
  const [state, setState] = useState<"upload" | "bind" | "preview" | "finish">(
    "upload"
  );
  const [bindingConfig, setBindingConfig] =
    useState<UserImportBindingConfig | null>(null);
  const [userDuplicates, setUserDuplicates] = useState<UserDuplicate[]>([]);
  const [usersData, setUsersData] = useState<UserImportData[]>([]);
  const [unprocessedUsersData, setUnprocessedUsersData] = useState<
    UserImportData[]
  >([]);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [importedRecordsCount, setImportedRecordsCount] = useState<number>(0);

  useEffect(() => {
    if (!importedFile) {
      setCsvHeaders([]);
    }
  }, [importedFile]);

  useEffect(() => {
    setBindingConfig(null);
  }, [csvHeaders]);

  useEffect(() => {
    setUsersData([]);
    setUnprocessedUsersData([]);
    setTotalRecords(0);
    setImportedRecordsCount(0);
  }, [bindingConfig]);

  const handleOpenChange = () => {
    if (state === "finish") {
      resetDialog();
    }
    setOpen(!open);
    // go back to start when closing
    if (open) setState("upload");
  };

  const resetDialog = () => {
    setImportedFile(null);
    setState("upload");
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button size={"xs"} className="align-middle">
          {" "}
          <p>Import Users</p>
          <CircleArrowUp className="w-4 ml-1" />
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Import Users</DialogTitle>
        </DialogHeader>
        {state == "upload" && (
          <UserImportUploadStepForm
            importedFile={importedFile}
            tenantId={tenantId}
            onCancel={() => {
              setImportedFile(null);
              setOpen(!open);
            }}
            onNext={() => setState("bind")}
            setImportedFile={setImportedFile}
            setHeaders={(value) => {
              setCsvHeaders(value);
            }}
          />
        )}
        {state == "bind" && (
          <UserImportBindColumnsStepForm
            bindingConfig={bindingConfig}
            fileId={importedFile?.id ?? ""}
            tenantId={tenantId}
            headers={csvHeaders}
            setConfig={(value) => setBindingConfig(value)}
            setDuplicates={setUserDuplicates}
            setPreview={setUsersData}
            setTotalRecords={setTotalRecords}
            onCancel={() => setState("upload")}
            onNext={() => setState("preview")}
          />
        )}
        {state == "preview" && bindingConfig && (
          <UserImportPreviewStepForm
            duplicates={userDuplicates}
            previews={usersData}
            totalRecords={totalRecords}
            tenantId={tenantId}
            fileId={importedFile?.id ?? ""}
            bindingConfig={bindingConfig}
            onCancel={() => setState("bind")}
            onNext={() => setState("finish")}
            setImportedRecords={setImportedRecordsCount}
            setTotalRecords={setTotalRecords}
            setUnprocessedUsersData={setUnprocessedUsersData}
          />
        )}
        {state == "finish" && (
          <UserImportFinishStep
            previews={unprocessedUsersData}
            totalRecords={totalRecords}
            onFinish={() => {
              resetDialog();
              setOpen(false);
            }}
            importedRecords={importedRecordsCount}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};
